import React from 'react';
import { ChatSession } from '../types';
import { MessageSquare, Trash2, Plus, X, Clock } from 'lucide-react';
import { cn } from '../utils/utils'; // Assuming cn utility exists from shadcn/ui or common utils

interface ChatSidebarProps {
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string, e: React.MouseEvent) => void;
  onNewChat: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  isOpen,
  onClose,
}) => {
  // Group sessions by date category
  const groupedSessions = React.useMemo(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups: Record<string, ChatSession[]> = {
      'Today': [],
      'Yesterday': [],
      'Previous 7 Days': [],
      'Older': [],
    };

    sessions.forEach(session => {
      const date = new Date(session.updatedAt);
      if (date.toDateString() === today.toDateString()) {
        groups['Today'].push(session);
      } else if (date.toDateString() === yesterday.toDateString()) {
        groups['Yesterday'].push(session);
      } else if (date > lastWeek) {
        groups['Previous 7 Days'].push(session);
      } else {
        groups['Older'].push(session);
      }
    });

    return groups;
  }, [sessions]);

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-200 ease-in-out flex flex-col",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "md:translate-x-0 md:static md:h-[calc(100vh-60px)] md:mt-[60px]" // Desktop specific styles
      )}>
        <div className="p-4 border-b flex items-center justify-between">
          <button
            onClick={onNewChat}
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus size={18} />
            <span>New Chat</span>
          </button>
          <button 
            onClick={onClose}
            className="md:hidden ml-2 p-2 hover:bg-gray-100 rounded-lg text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {Object.entries(groupedSessions).map(([group, groupSessions]) => (
            groupSessions.length > 0 && (
              <div key={group} className="mb-6">
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {group}
                </h3>
                <div className="space-y-1">
                  {groupSessions.map((session) => (
                    <button
                      key={session.id}
                      onClick={() => onSelectSession(session.id)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg text-sm flex items-center gap-3 transition-colors group relative",
                        currentSessionId === session.id 
                          ? "bg-green-50 text-green-700" 
                          : "hover:bg-gray-100 text-gray-700"
                      )}
                    >
                      <MessageSquare size={16} className={currentSessionId === session.id ? "text-green-600" : "text-gray-400"} />
                      <div className="flex-1 truncate pr-6">
                        <span className="font-medium block truncate">{session.title}</span>
                        <span className="text-xs text-gray-500 opacity-70">
                            {/* Optional: Add time or bill name here if needed */}
                        </span>
                      </div>
                      
                      <div 
                        onClick={(e) => onDeleteSession(session.id, e)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 hover:text-red-600 rounded-md transition-all text-gray-400"
                        title="Delete chat"
                        role="button"
                        aria-label="Delete chat"
                      >
                        <Trash2 size={14} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )
          ))}
          
          {sessions.length === 0 && (
            <div className="text-center py-8 text-gray-500 px-4">
              <Clock size={32} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">No chat history yet.</p>
              <p className="text-xs mt-1">Start a new conversation!</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatSidebar;
